import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet,
  Key,
  CreditCard,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Coins,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { walletService } from '../../services/walletService';
import type { Transaction } from '../../types/wallet';
import {
  formatCoins,
  formatCoinsDelta,
  formatTransactionDescription,
  formatWalletDate,
  getTransactionTypeLabel,
  isValidWalletCode,
} from '../../utils/walletLabels';

const CHARGE_PRESETS = [50, 100, 200, 500];

const PortalWallet: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [walletCode, setWalletCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [showRedeemSuccess, setShowRedeemSuccess] = useState(false);
  const [addedCoins, setAddedCoins] = useState(0);
  const [newBalanceAfterRedeem, setNewBalanceAfterRedeem] = useState(0);

  const [chargeAmount, setChargeAmount] = useState('');
  const [isCharging, setIsCharging] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);

  const loadWallet = useCallback(async (page = 1) => {
    setIsLoading(true);
    setLoadError(null);
    const result = await walletService.getMyHistory(page, 10);
    if (result.success) {
      setBalance(result.data.history.currentBalance);
      setTransactions(result.data.history.transactions);
      setPageNumber(result.data.pageNumber);
      setTotalPages(result.data.totalPages);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadWallet(1);
  }, [loadWallet]);

  useEffect(() => {
    if (!showRedeemSuccess) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowRedeemSuccess(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showRedeemSuccess]);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemError(null);
    const code = walletCode.trim();
    if (!isValidWalletCode(code)) {
      setRedeemError('كود المحفظة يجب أن يكون 7 أرقام');
      return;
    }
    setIsRedeeming(true);
    const result = await walletService.redeemWalletCode(code);
    setIsRedeeming(false);
    if (result.success) {
      setBalance(result.data.newBalance);
      setAddedCoins(result.data.addedCoins);
      setNewBalanceAfterRedeem(result.data.newBalance);
      setWalletCode('');
      setShowRedeemSuccess(true);
      loadWallet(1);
    } else {
      setRedeemError(result.error);
    }
  };

  const handleCharge = async (amount: number) => {
    if (amount <= 0) {
      setChargeError('يرجى إدخال مبلغ صحيح');
      return;
    }
    setChargeError(null);
    setIsCharging(true);
    const result = await walletService.chargeWallet(amount);
    setIsCharging(false);
    if (result.success && result.data.paymentSession.sessionUrl) {
      window.location.href = result.data.paymentSession.sessionUrl;
    } else {
      setChargeError(result.success ? 'لم يتم العثور على رابط الدفع' : result.error);
    }
  };

  const handleCustomCharge = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(chargeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setChargeError('يرجى إدخال مبلغ صحيح');
      return;
    }
    void handleCharge(amount);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 font-cairo sm:px-6 sm:py-8" dir="rtl">
      {showRedeemSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowRedeemSuccess(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-redeem-success-title"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-gold-600">
              <CheckCircle className="h-7 w-7" />
            </div>
            <h3 id="wallet-redeem-success-title" className="mb-1 text-lg font-bold text-[#001848]">
              تمام كده!
            </h3>
            <p className="text-sm text-slate-500">
              اتضاف <span className="font-semibold text-[#001848]">{formatCoins(addedCoins)}</span> جنيه
            </p>
            <p className="mt-3 text-sm text-slate-600">
              رصيدك: <span className="font-bold text-[#001848]">{formatCoins(newBalanceAfterRedeem)}</span>
            </p>
            <button
              type="button"
              onClick={() => setShowRedeemSuccess(false)}
              className="mt-5 min-h-11 w-full rounded-xl bg-[#001848] text-sm font-semibold text-white"
            >
              تمام
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#001848] sm:text-2xl">محفظتي</h1>
          <p className="mt-1 text-sm text-slate-500">رصيدك وشحن ومعاملاتك</p>
        </div>
        <button
          type="button"
          onClick={() => loadWallet(pageNumber)}
          disabled={isLoading}
          className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-[#001848] disabled:opacity-50"
          aria-label="تحديث"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Balance */}
      <div className="mb-5 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#001848] text-white">
          <Coins className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">الرصيد الحالي</p>
          {isLoading ? (
            <div className="mt-1 h-8 w-24 animate-pulse rounded bg-slate-100" />
          ) : (
            <p className="text-2xl font-bold text-[#001848] sm:text-3xl">
              {formatCoins(balance ?? 0)}
              <span className="ms-1.5 text-sm font-medium text-slate-400">جنيه</span>
            </p>
          )}
          <p className="mt-0.5 text-xs text-slate-400">الرصيد بالجنيه المصري</p>
        </div>
      </div>

      {loadError && (
        <div className="mb-5 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {loadError}
        </div>
      )}

      {/* Actions */}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <Key className="h-4 w-4 text-gold-600" />
            <h2 className="text-sm font-bold text-[#001848]">كود المحفظة</h2>
          </div>
          <form onSubmit={handleRedeem} className="space-y-2.5">
            <input
              type="text"
              inputMode="numeric"
              maxLength={7}
              value={walletCode}
              onChange={(e) => {
                setWalletCode(e.target.value.replace(/\D/g, '').slice(0, 7));
                setRedeemError(null);
              }}
              placeholder="0000000"
              className="min-h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-center font-mono text-lg tracking-widest text-[#001848] outline-none focus:border-[#001848] focus:bg-white"
              disabled={isRedeeming}
              autoComplete="off"
            />
            {redeemError && <p className="text-xs text-red-600">{redeemError}</p>}
            <button
              type="submit"
              disabled={isRedeeming || walletCode.length !== 7}
              className="min-h-11 w-full rounded-xl bg-gold-500 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isRedeeming ? 'جاري التحقق...' : 'استخدام الكود'}
            </button>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#001848]" />
            <h2 className="text-sm font-bold text-[#001848]">شحن بالدفع</h2>
          </div>
          <div className="mb-2.5 grid grid-cols-2 gap-2">
            {CHARGE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => void handleCharge(preset)}
                disabled={isCharging}
                className="min-h-10 rounded-xl border border-slate-200 text-sm font-semibold text-[#001848] hover:border-[#001848] disabled:opacity-50"
              >
                {formatCoins(preset)} ج
              </button>
            ))}
          </div>
          <form onSubmit={handleCustomCharge} className="flex gap-2">
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={chargeAmount}
              onChange={(e) => {
                setChargeAmount(e.target.value);
                setChargeError(null);
              }}
              placeholder="مبلغ"
              className="min-h-11 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-[#001848] focus:bg-white"
              disabled={isCharging}
            />
            <button
              type="submit"
              disabled={isCharging || !chargeAmount}
              className="min-h-11 rounded-xl bg-[#001848] px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              شحن
            </button>
          </form>
          {chargeError && <p className="mt-2 text-xs text-red-600">{chargeError}</p>}
        </div>
      </div>

      {/* Transactions */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <h2 className="text-sm font-bold text-[#001848]">سجل المعاملات</h2>
        </div>

        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-50" />
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Wallet className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">لا توجد معاملات بعد</p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {transactions.map((tx) => {
                const isCredit = tx.amount > 0;
                return (
                  <li key={tx.id} className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
                    <div
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isCredit ? 'bg-orange-50 text-gold-600' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {isCredit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-sm font-semibold text-[#001848]">
                        {formatTransactionDescription(tx.description, tx.type)}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-400">
                        {getTransactionTypeLabel(tx.type)} · {formatWalletDate(tx.createdAt)}
                      </p>
                    </div>
                    <div className="shrink-0 text-end">
                      <p className={`text-sm font-bold ${isCredit ? 'text-gold-600' : 'text-slate-700'}`}>
                        {formatCoinsDelta(tx.amount)}
                      </p>
                      <p className="text-[11px] text-slate-400">{formatCoins(tx.balanceAfter)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
                <button
                  type="button"
                  onClick={() => loadWallet(pageNumber - 1)}
                  disabled={pageNumber <= 1 || isLoading}
                  className="inline-flex min-h-10 items-center gap-1 rounded-lg px-3 text-sm text-[#001848] disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                  السابق
                </button>
                <span className="text-xs text-slate-500">
                  {pageNumber} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => loadWallet(pageNumber + 1)}
                  disabled={pageNumber >= totalPages || isLoading}
                  className="inline-flex min-h-10 items-center gap-1 rounded-lg px-3 text-sm text-[#001848] disabled:opacity-40"
                >
                  التالي
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PortalWallet;
